import React, { useEffect, useState } from "react"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import { NumberFieldSeeker, IconSeeker } from "@ui-seeker/core"
import { useValidatorSeeker } from "@app"
import { object, string } from "yup"
import useAutoEuropeDataInfo from "Pages/AutoEuropeTool/Page/@hooks/useDataInfo"
import { autoEuropeDrawerData, autoEuropeDrawerErrors } from "Pages/AutoEuropeTool/Page/@signals"
import ProviderOverrideModal from "Pages/AutoEuropeTool/Page/@components/ProviderOverrideModal"
import { dsColorNew } from "@palette"

// Shared field for Minimum Margin / Minimum Profit. Both are a numeric value with
// per-provider overrides that support duration exceptions (withDurations).
export default function AutoEuropeMinimumValueField({ field, label, placeholder, startIcon, max }) {
	const initial = autoEuropeDrawerData.peek()[field]
	const { dataInfo } = useAutoEuropeDataInfo(
		typeof initial === 'object' ? initial?.value ?? null : initial ?? null
	)
	const [modalOpen, setModalOpen] = useState(false)

	const { errors, isValid } = useValidatorSeeker({ [field]: dataInfo.value }, object({
		[field]: string()
			.required(`${label} is required`)
			.test("range", `Must be a value between 0.1 and ${max}`, v => v !== null && v !== "" && Number(v) >= 0.1 && Number(v) <= max)
	}))

	useEffect(() => {
		autoEuropeDrawerErrors.value = { ...autoEuropeDrawerErrors.value, [field]: isValid }
	}, [JSON.stringify(errors)])

	const handleValueChange = (e) => {
		const value = e.target.value
		dataInfo.value = value
		const current = autoEuropeDrawerData.value[field]
		autoEuropeDrawerData.value = {
			...autoEuropeDrawerData.value,
			[field]: { value, providers: current?.providers ?? [] },
		}
	}

	const handleProvidersChange = (providers) => {
		autoEuropeDrawerData.value = {
			...autoEuropeDrawerData.value,
			[field]: { ...autoEuropeDrawerData.value[field], providers },
		}
	}

	const providers = autoEuropeDrawerData.value[field]?.providers ?? []

	return (
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
			<Box sx={{ flex: 1 }}>
				<NumberFieldSeeker
					label={label}
					placeholder={placeholder}
					startIcon={startIcon}
					value={dataInfo.value ?? ""}
					onChange={handleValueChange}
					error={errors?.[field]}
				/>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
				<Tooltip title="Provider overrides">
					<IconButton size="small" onClick={() => setModalOpen(true)}>
						<IconSeeker icon="plus" fontSize="small"/>
					</IconButton>
				</Tooltip>
				{providers.length > 0 && (
					<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.appBlue }}>
						{providers.length}
					</Typography>
				)}
			</Box>
			<ProviderOverrideModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				title={`${label} — Provider overrides`}
				overrides={providers}
				onChange={handleProvidersChange}
				withDurations
			/>
		</Box>
	)
}
